import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { satisfaction as api } from '../services/api';

/**
 * Onglet « Charte » : l'en-tête du questionnaire aux couleurs de la société.
 * Une charte par plaque (2026-11-02, reprise par toutes ses concessions qui
 * n'ont pas la leur, réglée par le super-admin), une par concession
 * (s'applique à toutes ses sociétés) et, au besoin, une par société qui prend
 * le pas. Le serveur choisit la plus précise.
 */

const LOGO_MAX = 300 * 1024;
const TYPES_LOGO = ['image/png', 'image/jpeg', 'image/webp'];

// Même règle que la webapp : texte foncé sur fond clair, blanc sinon.
function texteSur(hex) {
  const m = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex || '');
  if (!m) return null;
  const lin = (v) => { const c = parseInt(v, 16) / 255; return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4; };
  const L = 0.2126 * lin(m[1]) + 0.7152 * lin(m[2]) + 0.0722 * lin(m[3]);
  return L > 0.4 ? 'var(--charte-texte-fonce)' : 'var(--charte-texte-clair)';
}

const fondParDefaut = () => {
  try {
    return getComputedStyle(document.documentElement).getPropertyValue('--charte-fond-defaut').trim();
  } catch { return ''; }
};

function ApercuEnTete({ nom, couleur, logo, site }) {
  const style = couleur ? { background: couleur, color: texteSur(couleur) } : undefined;
  return (
    <div className={`sat-charte-apercu${couleur ? '' : ' sat-charte-neutre'}`} style={style}>
      {logo ? <img src={logo} alt={nom} /> : <strong>{nom || 'Nom affiché'}</strong>}
      {site && <small>{site}</small>}
    </div>
  );
}

function CharteModal({ charte, concessionNom, societe, plaque, onClose, onSaved }) {
  const [nom, setNom] = useState(charte?.nom_affiche || '');
  const [couleur, setCouleur] = useState(charte?.couleur_fond || '');
  const [afficherSite, setAfficherSite] = useState(Boolean(charte?.afficher_site));
  // undefined = logo inchangé ; '' = retiré ; data URL = nouveau.
  const [logo, setLogo] = useState(undefined);
  const [erreur, setErreur] = useState('');
  const [saving, setSaving] = useState(false);

  const logoAffiche = logo === undefined
    ? (charte?.a_logo ? `/api/public/retours-prospects/charte/${charte.id}/logo?v=${new Date(charte.updated_at).getTime()}` : null)
    : logo || null;

  const choisirLogo = (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    if (!TYPES_LOGO.includes(f.type)) { setErreur('Logo : PNG, JPEG ou WebP uniquement (pas de SVG).'); return; }
    if (f.size > LOGO_MAX) { setErreur('Logo trop lourd : 300 Ko au plus.'); return; }
    setErreur('');
    const r = new FileReader();
    r.onload = () => setLogo(String(r.result));
    r.readAsDataURL(f);
  };

  const enregistrer = async () => {
    setSaving(true);
    setErreur('');
    try {
      await api.saveCharte({
        ...(plaque
          ? { plaque_id: plaque.id }
          : { concession_id: charte?.concession_id || societe.concession_id, code_societe: societe?.code_societe || null }),
        nom_affiche: nom, couleur_fond: couleur || null, afficher_site: afficherSite,
        ...(logo !== undefined && { logo }),
      });
      onSaved();
    } catch (e) {
      setErreur(e.response?.data?.error || 'Enregistrement impossible');
    } finally {
      setSaving(false);
    }
  };

  const portee = plaque
    ? `Plaque ${plaque.nom} — toutes ses concessions`
    : societe?.code_societe
      ? `Société ${societe.code_societe}${societe.sites ? ` (${societe.sites})` : ''}`
      : `${concessionNom} — toutes ses sociétés`;
  const exempleSite = societe?.sites?.split(' · ')[0] || 'Point de vente';

  return (
    <div className="modal-overlay">
      <div className="modal-content small" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>🎨 Charte — {portee}</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body sat-charte-form">
          <h5 className="sat-charte-titre">Aperçu de l'en-tête</h5>
          <ApercuEnTete nom={nom || concessionNom} couleur={couleur} logo={logoAffiche}
            site={afficherSite ? exempleSite : null} />

          <label className="sat-champ">
            <span>Nom affiché <small>(texte alternatif du logo, ou titre s'il n'y en a pas)</small></span>
            <input className="sat-input" value={nom} maxLength={120} placeholder={concessionNom}
              onChange={(e) => setNom(e.target.value)} />
          </label>

          <div className="sat-champ">
            <span>Logo</span>
            <div className="sat-charte-ligne">
              <label className="btn-secondary sat-charte-fichier">
                {logoAffiche ? 'Remplacer…' : 'Choisir un fichier…'}
                <input type="file" accept={TYPES_LOGO.join(',')} onChange={choisirLogo} hidden />
              </label>
              {logoAffiche && <button type="button" className="btn-secondary" onClick={() => setLogo('')}>Retirer</button>}
            </div>
            <p className="sat-aide">PNG, JPEG ou WebP, 300 Ko au plus. Idéalement en largeur, sur fond transparent.</p>
          </div>

          <div className="sat-champ">
            <span>Couleur de fond</span>
            <div className="sat-charte-ligne">
              <input type="color" className="sat-charte-couleur" value={couleur || fondParDefaut()}
                onChange={(e) => setCouleur(e.target.value)} />
              <input className="sat-input sat-charte-hex" value={couleur} placeholder="Neutre"
                onChange={(e) => setCouleur(e.target.value.trim())} />
              {couleur && <button type="button" className="btn-secondary" onClick={() => setCouleur('')}>Neutre</button>}
            </div>
            <p className="sat-aide">Le texte de l'en-tête passe en blanc ou en foncé selon le fond, pour rester lisible.</p>
          </div>

          <label className="sat-check">
            <input type="checkbox" checked={afficherSite} onChange={(e) => setAfficherSite(e.target.checked)} />
            Afficher le point de vente sous le logo (ex. « {exempleSite} »)
          </label>

          {erreur && <div className="status-error sat-bandeau">{erreur}</div>}
        </div>
        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>Annuler</button>
          <button className="btn-primary" onClick={enregistrer} disabled={saving}>
            {saving ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function SatisfactionCharte({ user, formulaires, editMode }) {
  // Les concessions proposées sont celles qui ont un formulaire : une charte
  // sans questionnaire n'aurait personne à habiller.
  const concessions = useMemo(() => {
    const m = new Map();
    for (const f of formulaires) if (!m.has(f.concession_id)) m.set(f.concession_id, f.concession_nom);
    return [...m.entries()].map(([id, nom]) => ({ id, nom })).sort((a, b) => a.nom.localeCompare(b.nom));
  }, [formulaires]);
  const [concId, setConcId] = useState(null);
  const [donnees, setDonnees] = useState({ chartes: [], societes: [], plaque: null });
  const [edition, setEdition] = useState(null); // { charte, societe }
  const [ajout, setAjout] = useState('');
  const [erreur, setErreur] = useState('');

  useEffect(() => {
    if (!concId && concessions.length) setConcId(concessions[0].id);
  }, [concessions, concId]);

  const charger = useCallback(async () => {
    if (!concId) return;
    try {
      const { data } = await api.getChartes({ concession_id: concId });
      setDonnees(data);
      setErreur('');
    } catch (e) {
      setErreur(e.response?.data?.error || 'Chargement impossible');
    }
  }, [concId]);
  useEffect(() => { charger(); }, [charger]);

  const concNom = concessions.find((c) => c.id === concId)?.nom || '';
  const parSociete = new Map(donnees.chartes.map((c) => [c.code_societe || '', c]));
  const sitesDe = (code) => donnees.societes.find((s) => s.code_societe === code)?.sites || '';
  const lignes = [
    { code_societe: null, charte: parSociete.get('') || null },
    ...donnees.chartes.filter((c) => c.code_societe).map((c) => ({ code_societe: c.code_societe, charte: c })),
  ];
  const ajoutables = donnees.societes.filter((s) => !parSociete.has(s.code_societe));
  const superAdmin = user?.role === 'super-admin';
  const peutSupprimer = editMode && superAdmin;
  const plaque = donnees.plaque;
  const chartePlaque = plaque?.charte || null;

  const supprimer = async (c) => {
    try {
      await api.deleteCharte(c.id);
      charger();
    } catch (e) {
      setErreur(e.response?.data?.error || 'Suppression impossible');
    }
  };

  const logoDe = (c) => (c?.a_logo ? `/api/public/retours-prospects/charte/${c.id}/logo?v=${new Date(c.updated_at).getTime()}` : null);

  if (!concessions.length) {
    return <div className="sat-vide">Créez d'abord un formulaire : la charte habille ses questionnaires.</div>;
  }

  return (
    <>
      <div className="page-toolbar">
        {concessions.length > 1 && (
          <select className="page-select" value={concId || ''} onChange={(e) => setConcId(Number(e.target.value))}>
            {concessions.map((c) => <option key={c.id} value={c.id}>{c.nom}</option>)}
          </select>
        )}
        {ajoutables.length > 0 && (
          <select className="page-select" value={ajout} onChange={(e) => {
            const s = donnees.societes.find((x) => x.code_societe === e.target.value);
            setAjout('');
            if (s) setEdition({ charte: null, societe: { ...s, concession_id: concId } });
          }}>
            <option value="">Charte propre à une société…</option>
            {ajoutables.map((s) => <option key={s.code_societe} value={s.code_societe}>{s.code_societe}{s.sites ? ` — ${s.sites}` : ''}</option>)}
          </select>
        )}
      </div>

      <p className="sat-aide">
        La charte de <b>{concNom}</b> habille l'en-tête de tous ses questionnaires.
        {plaque && <> Sans charte propre, la concession reprend celle de la plaque <b>{plaque.nom}</b> : la régler une fois suffit pour toutes ses concessions.</>}
        {' '}Une société n'a besoin de sa propre charte que si elle doit s'en distinguer.
      </p>
      {erreur && <div className="status-error sat-bandeau">{erreur}</div>}

      <div className="table-container">
        <table>
          <thead>
            <tr><th>Portée</th><th>En-tête</th><th>Point de vente affiché</th><th /></tr>
          </thead>
          <tbody>
            {plaque && (
              <tr>
                <td><b>Plaque {plaque.nom}</b><br /><small>Toutes les concessions sans charte propre</small></td>
                <td>
                  {chartePlaque
                    ? <ApercuEnTete nom={chartePlaque.nom_affiche || plaque.nom} couleur={chartePlaque.couleur_fond} logo={logoDe(chartePlaque)} />
                    : <span className="sat-muet">Aucune</span>}
                </td>
                <td>{chartePlaque ? (chartePlaque.afficher_site ? 'Oui' : 'Non') : '—'}</td>
                <td>
                  {superAdmin && (
                    <div className="action-buttons">
                      <button className="btn-icon" title={chartePlaque ? 'Modifier la charte de la plaque' : 'Créer la charte de la plaque'}
                        onClick={() => setEdition({ charte: chartePlaque, plaque })}>✏️</button>
                      {chartePlaque && peutSupprimer && (
                        <button className="btn-icon delete" title="Supprimer la charte de la plaque" onClick={() => supprimer(chartePlaque)}>🗑️</button>
                      )}
                    </div>
                  )}
                </td>
              </tr>
            )}
            {lignes.map(({ code_societe: code, charte }) => (
              <tr key={code || 'defaut'}>
                <td>
                  {code ? <><b>Société {code}</b><br /><small>{sitesDe(code)}</small></>
                    : <><b>{concNom}</b><br /><small>Par défaut, toutes les sociétés</small></>}
                </td>
                <td>
                  {charte
                    ? <ApercuEnTete nom={charte.nom_affiche || concNom} couleur={charte.couleur_fond} logo={logoDe(charte)} />
                    : code
                      ? <span className="sat-muet">—</span>
                      : chartePlaque
                        ? <span className="sat-muet">Charte de la plaque {plaque.nom} utilisée</span>
                        : <span className="sat-muet">En-tête neutre (nom de la concession)</span>}
                </td>
                <td>{charte ? (charte.afficher_site ? 'Oui' : 'Non') : '—'}</td>
                <td>
                  <div className="action-buttons">
                    <button className="btn-icon" title={charte ? 'Modifier' : 'Créer la charte'}
                      onClick={() => setEdition({ charte, societe: { code_societe: code, sites: sitesDe(code), concession_id: concId } })}>✏️</button>
                    {charte && peutSupprimer && (
                      <button className="btn-icon delete" title="Supprimer la charte" onClick={() => supprimer(charte)}>🗑️</button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {edition && (
        <CharteModal charte={edition.charte} societe={edition.societe} plaque={edition.plaque} concessionNom={edition.plaque ? edition.plaque.nom : concNom}
          onClose={() => setEdition(null)} onSaved={() => { setEdition(null); charger(); }} />
      )}
    </>
  );
}
